import type {
  AchievementId,
  BoxInstanceId,
  ChapterId,
  DialogueId,
  InstanceId,
  ItemId,
  NpcId,
  PhotoFrameId,
  ProductId,
  RoomId,
  SceneId,
  StickerId,
  TaskId,
  TitleId,
  TxId,
  UnlockId,
  WallFloorId,
} from '@/core/content/ids';
import type { PlacementTarget } from '@/core/domain/placement/rules';
import type { NeighborhoodStoryId } from '@/core/content/schemas/neighborhood';
import type { MiniGameResult } from '@/core/content/schemas/minigames';
import type { DomainEvent } from '@/core/events/types';
import type { AvatarState, CameraState, GameSave, SettingsState } from '@/core/persistence/schema/v1';

/**
 * Commands (content-schemas §13.1): plain serialisable objects. UI and Pixi controllers only ever call
 * `dispatch(command)`. The union grows per milestone; names and shapes follow the spec.
 */
export type Command =
  | { type: 'advanceNeighborhood'; storyId: NeighborhoodStoryId; stage: number }
  | { type: 'placeItem'; instanceId: InstanceId; target: PlacementTarget }
  /** `reverse`: Shift+R turns the other way (ux-screens §6.3). */
  | { type: 'rotateItem'; instanceId: InstanceId; reverse?: boolean }
  | { type: 'storeItem'; instanceId: InstanceId }
  | {
      type: 'displayProduct';
      roomId: RoomId;
      itemId: ProductId;
      parentInstanceId: InstanceId;
      slotIndex: number;
    }
  | { type: 'undisplayProduct'; roomId: RoomId; slotKey: `i_${string}#${number}` }
  | {
      type: 'moveProduct';
      roomId: RoomId;
      from: `i_${string}#${number}`;
      to: { parentInstanceId: InstanceId; slotIndex: number };
    }
  | { type: 'toggleShopOpen'; open: boolean }
  | { type: 'fulfillOrder'; txId: TxId; orderId: `o_${string}` }
  | { type: 'skipOrder'; orderId: `o_${string}` }
  /** Studio NANO (studio-nano.md §4): put a booking on the set, take the photo, say bye. */
  | { type: 'startShoot'; requestId: `sr_${string}` }
  | { type: 'cancelShoot' }
  | { type: 'finishShoot'; txId: TxId }
  | { type: 'endShoot' }
  | { type: 'skipShootRequest'; requestId: `sr_${string}` }
  | { type: 'openBox'; boxInstanceId: BoxInstanceId }
  | { type: 'finishMiniGame'; result: MiniGameResult }
  | { type: 'payUnlock'; txId: TxId; unlockId: UnlockId }
  | { type: 'buyItem'; txId: TxId; itemId: Exclude<ItemId, ProductId>; qty: number }
  | { type: 'buyStock'; txId: TxId; itemId: ProductId; boxes: number }
  | { type: 'buyWallFloor'; txId: TxId; wallFloorId: WallFloorId }
  | { type: 'applyWallFloor'; roomId: RoomId; wallFloorId: WallFloorId }
  | {
      type: 'claimReward';
      source:
        | { kind: 'achievement'; achievementId: AchievementId }
        | { kind: 'task'; taskId: TaskId; instanceKey?: string };
    }
  | { type: 'swapSuggestion'; taskId: TaskId; instanceKey?: string }
  | { type: 'markChapterSeen'; chapterId: ChapterId; skipped: boolean }
  | { type: 'claimMail'; mailId: string }
  | { type: 'talkToNpc'; npcId: NpcId; initiatedBy?: 'player' | 'visit' }
  | { type: 'petCat' }
  /** The photo's pixels are already in the `photos` table (content-schemas §13). */
  | {
      type: 'takePhoto';
      photoId: `ph_${string}`;
      sceneId: SceneId;
      frameId?: PhotoFrameId;
      stickerIds: StickerId[];
    }
  | { type: 'deletePhoto'; photoId: `ph_${string}` }
  | { type: 'setAvatar'; patch: Partial<AvatarState> }
  | { type: 'equipTitle'; titleId: TitleId | null }
  | { type: 'advanceDialogue'; dialogueId: DialogueId; nodeId: string; choiceIndex?: number }
  | { type: 'updateSettings'; patch: Partial<SettingsState> }
  /** S06: these catalog entries were seen, drop their "Mới" badge. */
  | { type: 'markCatalogSeen'; ids: string[] }
  /** ★ in S05 / S07: a player bookmark, no gameplay effect. */
  | { type: 'toggleFavorite'; itemId: ItemId }
  | { type: 'navigateScene'; sceneId: SceneId }
  | {
      type: 'updateLastSession';
      patch: {
        cameras?: Partial<Record<SceneId, CameraState>>;
        /** The "durable" panel to reopen on the next visit (ux-screens §7.1), with its params (e.g. tab). */
        panel?: GameSave['lastSession']['panel'];
        panelParams?: Record<string, string>;
      };
    }
  | { type: 'advanceTime'; dtMs: number; nowWallMs: number }
  /** On load / return to foreground: reconcile derived state with current content (content-schemas §11.6). */
  | { type: 'bootSession'; nowWallMs: number };

export type CommandType = Command['type'];
export type CommandOf<T extends CommandType> = Extract<Command, { type: T }>;

export type DomainErrorCode =
  | 'NOT_FOUND'
  | 'LOCKED'
  | 'INVALID_PLACEMENT'
  | 'SLOT_OCCUPIED'
  | 'NOT_ACCEPTED'
  | 'NOT_REMOVABLE'
  | 'INSUFFICIENT_COINS'
  | 'INSUFFICIENT_STOCK'
  | 'NOT_COMPLETED'
  | 'ALREADY_CLAIMED'
  | 'CONDITIONS_NOT_MET'
  | 'LIMIT_REACHED'
  | 'COOLDOWN'
  | 'INVALID_ARGUMENT';

export type CommandResult =
  | { ok: true; events: DomainEvent[]; replayed?: boolean }
  | { ok: false; error: DomainErrorCode; detail?: Record<string, unknown> };

/** Commands that must flush quickly (assets changed hands). Everything else is debounced. */
export const CRITICAL_COMMANDS: ReadonlySet<CommandType> = new Set<CommandType>([
  'advanceNeighborhood',
  'openBox',
  'fulfillOrder',
  'finishShoot',
  'claimMail',
  'finishMiniGame',
  'payUnlock',
  'claimReward',
  'buyItem',
  'buyStock',
  'buyWallFloor',
  'takePhoto',
]);

/** Commands that do not need a save at all. */
export const EPHEMERAL_COMMANDS: ReadonlySet<CommandType> = new Set<CommandType>([]);

export interface TxCommand {
  txId: TxId;
}

/** For `undo/redo` stacks (Milestone 1): commands that rearrange a room. */
export const ORGANIZING_COMMANDS: ReadonlySet<CommandType> = new Set<CommandType>([
  'placeItem',
  'rotateItem',
  'storeItem',
]);

export type RoomScoped = { roomId: RoomId };
